import React from 'react';
import { useTranslation } from 'react-i18next';
import useCampaignSpendStatus from '../../../hooks/stats/useCampaignSpendStatus';
import { formatNumber } from '../../../utils/functions';
import {
    PACING_STATES,
    formatPercent,
    formatPoints,
    formatRatio,
    isEvenlyMode,
} from '../../../utils/spendPacing';
import PacingPill from '../../SpendPacing/PacingPill';

const I18N = 'sections.spend-pacing.panel';

const ERROR_KEYS = {
    BEFORE_CAMPAIGN_START: `${I18N}.errors.before-start`,
    AFTER_CAMPAIGN_END: `${I18N}.errors.after-end`,
    PACING_DATA_UNAVAILABLE: `${I18N}.errors.unavailable`,
};

const formatAmount = (value, currency) =>
    value === null || value === undefined
        ? '—'
        : `${currency} ${formatNumber(value, 'properCurrency', undefined, {
              currency,
          })}`;

const StatePill = ({ state, size }) =>
    PACING_STATES[state] ? <PacingPill state={state} size={size} /> : null;

const Block = ({ title, testId, children }) => (
    <section
        className="flex flex-col space-y-1 p-3 rounded-lg bg-gray-50"
        data-testid={testId}
    >
        <h3 className="font-light text-xs uppercase text-gray-600">{title}</h3>
        {children}
    </section>
);

/**
 * "Estado de compra" for an Evenly campaign at the current hour, from
 * dsp-stats-v2 spend-status. "Ayer" is real spend; "hoy" is the scheduler's
 * plan (spend so far plus the remaining forecast).
 */
function SpendPacingPanel({ campaign }) {
    const { t } = useTranslation();
    const isEvenly = isEvenlyMode(campaign?.scheduler_mode);
    const { data, error, isLoading } = useCampaignSpendStatus(campaign?.id, {
        enabled: isEvenly,
    });

    if (!isEvenly) return null;

    const status = data?.status;
    const daily = status?.daily;
    const projection = status?.daily_projection;
    const pace = status?.pace;
    const lastDay = pace?.last_day;
    const today = pace?.today;
    const currency = data?.currency ?? '';

    const header = (
        <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-baseline gap-3">
                <h2 className="font-light text-normal uppercase">
                    {t(`${I18N}.title`)}
                </h2>
                {daily && (
                    <span className="text-sm text-gray-500">
                        {t(`${I18N}.flight-day`, {
                            day: daily.elapsed,
                            total: daily.total,
                        })}
                    </span>
                )}
            </div>
            {daily && (
                <div className="flex items-center gap-2">
                    <StatePill state={daily.state} size="sm" />
                    <PacingPill
                        kind="correction"
                        state={pace?.correction ?? null}
                        size="sm"
                        title={t(`${I18N}.correction-title`)}
                    />
                </div>
            )}
        </div>
    );

    if (isLoading) {
        return (
            <div
                className="module w-full my-2"
                data-testid="spend-pacing-panel"
                aria-busy="true"
            >
                {header}
            </div>
        );
    }

    if (error || !daily) {
        return (
            <div
                className="module w-full my-2"
                data-testid="spend-pacing-panel"
            >
                {header}
                <p className="mt-2 text-sm text-gray-600" role="status">
                    {t(ERROR_KEYS[error?.code] || `${I18N}.errors.generic`)}
                </p>
            </div>
        );
    }

    const todayHasForecast = today && today.forecast_available !== false;

    return (
        <div className="module w-full my-2" data-testid="spend-pacing-panel">
            {header}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                <Block
                    title={t(`${I18N}.cumulative-title`)}
                    testId="pacing-cumulative"
                >
                    <div className="flex items-center gap-2">
                        <StatePill state={daily.state} />
                        <span className="font-mono text-sm">
                            {t(`${I18N}.points`, {
                                points: formatPoints(daily.deviation_pct),
                            })}
                        </span>
                    </div>
                    <p className="text-sm">
                        {t(`${I18N}.spent-of-budget`, {
                            spent: formatPercent(daily.spent_pct),
                            expected: formatPercent(daily.expected_pct),
                        })}
                    </p>
                </Block>

                <Block
                    title={t(`${I18N}.yesterday-title`)}
                    testId="pacing-yesterday"
                >
                    {lastDay ? (
                        <>
                            <div className="flex items-center gap-2">
                                <StatePill state={lastDay.state} />
                            </div>
                            <p className="text-sm">
                                {t(`${I18N}.of-required`, {
                                    ratio: formatRatio(lastDay.pace_ratio),
                                })}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">
                            {t(`${I18N}.no-closed-day`)}
                        </p>
                    )}
                </Block>

                <Block title={t(`${I18N}.today-title`)} testId="pacing-today">
                    {todayHasForecast ? (
                        <>
                            <div className="flex items-center gap-2">
                                <StatePill state={today.state} />
                                <span className="text-xs italic text-gray-500">
                                    {t(`${I18N}.projected-badge`)}
                                </span>
                            </div>
                            <p className="text-sm">
                                {t(`${I18N}.of-required`, {
                                    ratio: formatRatio(today.pace_ratio),
                                })}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">
                            {t(`${I18N}.no-forecast`)}
                        </p>
                    )}
                </Block>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-2 text-xs text-gray-600">
                {projection && (
                    <span
                        className="flex items-center gap-2"
                        data-testid="pacing-projection"
                    >
                        {t(`${I18N}.projection-title`, { day: projection.day })}
                        :
                        {projection.forecast_available === false ? (
                            <span>{t(`${I18N}.projection-no-forecast`)}</span>
                        ) : (
                            <>
                                <span>
                                    {t(`${I18N}.projection-value`, {
                                        projected: formatAmount(
                                            projection.projected_spent,
                                            currency
                                        ),
                                        expected: formatAmount(
                                            projection.expected_spent,
                                            currency
                                        ),
                                    })}
                                </span>
                                <span className="font-mono">
                                    {t(`${I18N}.points`, {
                                        points: formatPoints(
                                            projection.deviation_pct
                                        ),
                                    })}
                                </span>
                                <StatePill state={projection.state} />
                            </>
                        )}
                    </span>
                )}
            </div>
        </div>
    );
}

export default SpendPacingPanel;
